import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Download, RotateCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  useDocumentTreeQuery,
  useDocumentStatusQuery,
  useReprocessDocumentMutation,
} from '@/lib/api/documents';
import { cn } from '@/lib/utils/cn';

/**
 * T-0114 / E02-F02 — Parse-tree viewer (read-only).
 *
 * Visual contract:
 *   - `design_handoff_caliber_v2/prototype/modals-d3.js → openParseTree`
 *   - README §4 modal table row `data-open-parse`
 *
 * Behavior:
 *   - Renders the parsed-document markdown tree (T-0041) — a left-side
 *     hierarchical outline that the consultant can scroll/jump-through,
 *     alongside a stage view that approximates how the parsed sections
 *     read. The backend returns a single markdown string today
 *     (`apps/api/src/services/documents.ts → getDocumentTree`); the modal
 *     parses heading lines (`# `, `## `, `### `) to build the outline.
 *   - Status banner reads `useDocumentStatusQuery` and shows
 *     parse/index/classification states alongside the outline.
 *   - "Reprocess" calls the T-0042 reprocess endpoint, which kicks the
 *     pipeline to re-extract. The button is destructive-tinted because
 *     it re-emits ingestion events; consultants are prompted before.
 *
 * Unlike the four config editors this modal is read-only — no draft /
 * save state, no dirty-state guard. The shell is a centered dialog
 * (not the right drawer) per the prototype's `.parse-modal` shape.
 */

export interface ParseTreeModalProps {
  documentId: string;
  engagementId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TreeNode {
  level: 1 | 2 | 3;
  text: string;
  line: number;
}

function parseTreeFromMarkdown(md: string): TreeNode[] {
  const out: TreeNode[] = [];
  const lines = md.split('\n');
  lines.forEach((raw, i) => {
    const line = raw.trimStart();
    if (line.startsWith('### ')) {
      out.push({ level: 3, text: line.slice(4).trim(), line: i });
    } else if (line.startsWith('## ')) {
      out.push({ level: 2, text: line.slice(3).trim(), line: i });
    } else if (line.startsWith('# ')) {
      out.push({ level: 1, text: line.slice(2).trim(), line: i });
    }
  });
  return out;
}

export function ParseTreeModal({
  documentId,
  engagementId,
  open,
  onOpenChange,
}: ParseTreeModalProps) {
  const tree = useDocumentTreeQuery(open ? documentId : undefined);
  const status = useDocumentStatusQuery(open ? documentId : undefined);
  const reprocess = useReprocessDocumentMutation(engagementId);
  const [reprocessError, setReprocessError] = useState<unknown>(null);
  const [reprocessSubmitted, setReprocessSubmitted] = useState(false);

  const outline = useMemo<TreeNode[]>(() => {
    if (!tree.data?.markdown_tree) return [];
    return parseTreeFromMarkdown(tree.data.markdown_tree);
  }, [tree.data]);

  const handleReprocess = async () => {
    setReprocessError(null);
    const ok = window.confirm(
      'Reprocess this document? The ingestion pipeline will re-extract the markdown tree and re-emit downstream events.',
    );
    if (!ok) return;
    try {
      await reprocess.mutateAsync(documentId);
      setReprocessSubmitted(true);
    } catch (err) {
      setReprocessError(err);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby="parse-tree-title"
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(1100px,94vw)] max-h-[88vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <header className="flex items-start gap-3 border-b border-ink-100 px-6 py-4">
            <div className="min-w-0 flex-1">
              <div className="text-meta uppercase tracking-wide text-ink-500">
                Parsed document tree
              </div>
              <Dialog.Title
                id="parse-tree-title"
                className="mt-1 truncate text-h2 font-semibold text-ink-900"
              >
                Document parse preview
              </Dialog.Title>
              <p className="mt-1 text-meta text-ink-500">
                Inspect the markdown extraction tree, jump to sections, and reprocess on demand.
              </p>
            </div>
            <div className="flex flex-none items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!tree.data?.markdown_tree}
                onClick={() => {
                  if (!tree.data?.markdown_tree) return;
                  const blob = new Blob([tree.data.markdown_tree], { type: 'text/markdown' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${documentId.slice(0, 8)}-parse-tree.md`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                Export markdown
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={reprocess.isPending}
                onClick={handleReprocess}
                className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
              >
                <RotateCw
                  className={cn('h-3.5 w-3.5', reprocess.isPending && 'animate-spin')}
                  aria-hidden="true"
                />
                {reprocess.isPending ? 'Reprocessing…' : 'Reprocess'}
              </Button>
              <Dialog.Close
                aria-label="Close parse tree"
                className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Dialog.Close>
            </div>
          </header>

          <div className="grid grid-cols-[280px,1fr] overflow-hidden">
            <aside className="overflow-y-auto border-r border-ink-100 bg-paper-50 px-3 py-3">
              <div className="mb-2 text-meta uppercase tracking-wide text-ink-500">Outline</div>
              {tree.isLoading ? (
                <div className="flex items-center gap-2 text-body-sm text-ink-500">
                  <Spinner /> Loading tree…
                </div>
              ) : tree.error ? (
                <ErrorMessage error={tree.error} />
              ) : outline.length === 0 ? (
                <div className="text-body-sm text-ink-500">
                  No headings detected. The parser may still be running; check the status banner.
                </div>
              ) : (
                <ul className="space-y-1">
                  {outline.map((n, i) => (
                    <li key={i}>
                      <a
                        href={`#parse-node-${i}`}
                        className={cn(
                          'block truncate rounded px-2 py-1 text-body-sm hover:bg-paper-100',
                          n.level === 1 && 'font-semibold text-ink-900',
                          n.level === 2 && 'pl-4 text-ink-700',
                          n.level === 3 && 'pl-7 text-meta text-ink-500',
                        )}
                      >
                        {n.text}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </aside>

            <section className="overflow-y-auto px-6 py-4">
              <StatusBanner
                status={status.data}
                loading={status.isLoading}
                reprocessSubmitted={reprocessSubmitted}
                reprocessError={reprocessError}
              />
              {tree.isLoading ? (
                <div className="mt-4 flex items-center gap-2 text-body-sm text-ink-500">
                  <Spinner /> Loading parse tree…
                </div>
              ) : tree.error ? (
                <div className="mt-4">
                  <ErrorMessage error={tree.error} />
                </div>
              ) : tree.data ? (
                <article className="mt-4 max-w-[820px]">
                  <pre className="whitespace-pre-wrap rounded-card border border-ink-100 bg-paper-50 p-4 font-mono text-meta text-ink-700">
                    {tree.data.markdown_tree.length > 0
                      ? tree.data.markdown_tree
                      : '(empty tree — pipeline may still be running)'}
                  </pre>
                  <p className="mt-3 text-meta text-ink-500">
                    Each section&apos;s text is kept with its place in the document and indexed for
                    search. Citations from chat and AI proposals point here. To resolve
                    OCR-confidence concerns or stale section labels, click Reprocess.
                  </p>
                </article>
              ) : null}
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function StatusBanner({
  status,
  loading,
  reprocessSubmitted,
  reprocessError,
}: {
  status:
    | {
        upload_status?: string;
        parse_status?: string | null;
        index_status?: string | null;
        classification_status?: string | null;
        failure_reason?: string | null;
      }
    | undefined;
  loading: boolean;
  reprocessSubmitted: boolean;
  reprocessError: unknown;
}) {
  if (loading) {
    return (
      <div className="rounded-card border border-ink-100 bg-paper-50 px-4 py-2 text-body-sm text-ink-500">
        <Spinner /> Loading processing status…
      </div>
    );
  }
  if (!status) return null;
  const parseFailed = status.parse_status === 'failed';
  // T-0160 real status values: parse pending | parsing | parsed | failed;
  // index pending | indexing | indexed | failed | partial.
  const RUNNING = new Set(['in_progress', 'pending', 'parsing', 'indexing']);
  const stillRunning =
    RUNNING.has(status.parse_status ?? '') ||
    RUNNING.has(status.index_status ?? '') ||
    status.classification_status === 'in_progress';
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-card border px-4 py-2 text-body-sm',
        parseFailed
          ? 'border-posture-critical-tint bg-posture-critical-tint text-posture-critical'
          : stillRunning
            ? 'border-posture-at-risk-tint bg-posture-at-risk-tint text-posture-at-risk'
            : 'border-posture-healthy-tint bg-posture-healthy-tint text-posture-healthy',
      )}
    >
      {parseFailed ? <AlertTriangle className="h-4 w-4" aria-hidden="true" /> : null}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span>
          <span className="text-ink-500">upload</span>{' '}
          <span className="font-mono">{status.upload_status ?? '—'}</span>
        </span>
        <span>
          <span className="text-ink-500">parse</span>{' '}
          <span className="font-mono">{status.parse_status ?? '—'}</span>
        </span>
        <span>
          <span className="text-ink-500">index</span>{' '}
          <span className="font-mono">{status.index_status ?? '—'}</span>
        </span>
        <span>
          <span className="text-ink-500">classification</span>{' '}
          <span className="font-mono">{status.classification_status ?? '—'}</span>
        </span>
      </div>
      {status.failure_reason ? (
        <span className="basis-full text-meta">{status.failure_reason}</span>
      ) : null}
      {reprocessSubmitted ? (
        <span className="ml-auto text-meta text-ink-700">
          Reprocess submitted — refresh the page or close + reopen to see the next run.
        </span>
      ) : null}
      {reprocessError ? (
        <div className="basis-full">
          <ErrorMessage error={reprocessError} />
        </div>
      ) : null}
    </div>
  );
}
